// The one place a failure is rendered.
//
// # A server-authored sentence cannot be translated in the browser
//
// Riksu answers every failure with RFC 9457 `application/problem+json`, and its
// `detail` is written by riksu, in English, about *this instance* — "riksu cannot
// count leave days without an effective-dated working week for this workplace".
// Nothing in this bundle can translate that.
//
// What it *can* translate is the CLASS, and the contract already says which field
// names one: `type` is "the primary identifier a client branches on per RFC 9457",
// and `title` "describes the class, not this instance". So the renderer keys its own
// message on `type`, and falls back to riksu's words when it has no message for that
// class — saying, in the reader's language, that what follows is English.
//
// The alternative — translating `detail` server-side — would mean riksu holding a
// message catalogue, which puts natural language in a repository whose floor forbids
// it and puts localisation in the component least able to know who is reading.
//
// # A CLASS MESSAGE MAY ONLY CLAIM WHAT THE CLASS CARRIES
//
// `problem.conflict` said "Something else changed this first. Reload and try again." —
// a CAUSE and a REMEDY, neither of which a 409 carries. The contract describes the class
// as "a duplicate, a lost update, or an in-flight retry", for which that sentence is
// exactly right; riksu also answers 409 for domain refusals that will never succeed on
// retry — a document about the wrong person (0069/0080), a payroll period overlap, an
// employment with no contracted week. The product owner was told to reload a screen whose
// refusal is permanent, directly above riksu's own sentence saying why it is permanent.
//
// Ordinary unauthorized and additional-verification problems now have separate
// types under 401; only the former says the person is not signed in.
//
// THE FIX IS NOT TO DROP THE CLASS LINE WHEN A `detail` IS PRESENT, which is the obvious
// move and is wrong twice. riksu writes a `detail` on essentially every refusal, so that
// condition is "always" wearing a disguise — and the class line is the only sentence a
// reader who does not read English gets, which is the whole argument below for showing
// both. It would fix a false sentence by removing the one translated sentence.
//
// ErrorCode carries the problem class independently of status: Conflict and
// RuleRefusal both use409 and retain distinct codes/types in riksu-core/error.rs.
// The previous comment claimed code came from status and these were inseparable;
// the current implementation contradicts it. Never infer a moved observation
// from HTTP409 alone. A form that owns a retained opening may supply its explicit
// conflict recovery; other classes keep their normal message and every server
// detail remains verbatim. messageFor still matches the final type path segment.

import type { Problem } from "../lib/api";
import { ApiError } from "../lib/api";
import { type MessageKey, useT } from "./i18n";

export interface ProblemNoticeProps {
  /** A nested record's current router destination; the identity owner validates it. */
  readonly returnTo?: string;
  /** Narrow OAuth entry for a portal session; ordinary screens use the employee entry. */
  readonly signInAt?: "/auth/candidate-login";
  readonly failure: unknown;
  /** What the user was doing. Used for the heading when riksu named no title. */
  readonly whileDoing?: string;
  /** Recovery owned by this opening, used only for an explicit conflict problem. */
  readonly conflictRecovery?: string;
}

/** The problem behind any thrown value, or `null` if it is not one riksu wrote. */
export function problemOf(failure: unknown): Problem | null {
  return failure instanceof ApiError ? failure.problem : null;
}

/**
 * The message for a problem class, or `null` when this bundle has none.
 *
 * Matched on the LAST path segment of the `type` URI rather than the whole URI, so a
 * deployment served from another host does not lose every translation. The URI is
 * dereferenceable by design; its final segment is the class name.
 */
export function messageFor(type: string | undefined): MessageKey | null {
  // THE KEYS ARE `ErrorCode::as_str`'s SPELLING, because the segment is that string:
  // riksu builds the URI as `PROBLEM_TYPE_BASE + code.as_str()`. `not_found` carried a
  // HYPHEN here and riksu has always sent an underscore, so `problem.notFound` never
  // once rendered — every 404 in the product showed riksu's English sentence with no
  // translated line above it, which is the one thing a reader who does not read English
  // gets. Nothing was red: an unknown segment answers `null`, which is a designed and
  // silent degradation, so the only evidence was a missing sentence nobody was looking
  // for. A key that matches nothing is indistinguishable from a class riksu never sends.
  // KEYED ON THE CONTRACT'S OWN UNION, and that is the gate rather than a convenience.
  //
  // It was `Record<string, MessageKey>` and held SIX of riksu's TEN classes, so
  // `invalid_request`, `unprocessable`, `rate_limited` and `internal` fell through to
  // `null` — which is the designed silent degradation, and degrading to riksu's ENGLISH
  // title is the box the product owner read as absurd. Two of the four are what a mistyped
  // field comes back as, so this was not a rare path.
  //
  // `Problem["code"]` is projected from the OpenAPI document, so `Record<…>` refuses BOTH
  // directions at compile time: a class riksu adds and this bundle has no sentence for is a
  // `tsc` error naming the missing key, and an entry naming a class riksu does not send is
  // an excess-property error. **That is why there is no test here** — a test restating what
  // the compiler already refuses is a worse copy of a check that fails the build, which is
  // the rule `label` on `DataTable` is enforced by.
  //
  // The wire segment IS this spelling: riksu builds the URI as
  // `PROBLEM_TYPE_BASE + ErrorCode::as_str()`, and `as_str` is the snake_case `code`.
  const known: Record<Problem["code"], MessageKey> = {
    invalid_request: "problem.invalidRequest",
    unauthorized: "problem.unauthorized",
    insufficient_user_authentication: "problem.verificationRequired",
    forbidden: "problem.forbidden",
    not_found: "problem.notFound",
    conflict: "problem.conflict",
    rule_refusal: "problem.ruleRefusal",
    unprocessable: "problem.unprocessable",
    rate_limited: "problem.rateLimited",
    unavailable: "problem.unavailable",
    calculation_interrupted: "problem.calculationInterrupted",
    internal: "problem.internal",
  };
  if (!type) {
    return null;
  }
  // THE DECLARATION IS EXHAUSTIVE AND THE LOOKUP IS NOT, which is the honest split rather
  // than a cast to get past the compiler. `known` is total over what riksu's contract
  // DESCRIBES; the segment is whatever a deployment actually SENT, and a riksu newer than
  // this bundle can name a class no type here knows. Widening for the read keeps the
  // exhaustiveness where it does work — on the declaration — and answers `null` for the
  // one case it cannot cover, which is the degradation this function has always had.
  const lookup: Partial<Record<string, MessageKey>> = known;
  const segment = type.split("/").filter(Boolean).pop() ?? "";
  return lookup[segment] ?? null;
}

/**
 * RIKSU WRITES ITS REFUSALS, and this is where they land.
 *
 * "The denial names what would have worked, never what you are" is the rule the whole
 * authorization layer is built on — and the notice carrying that sentence rendered as
 * plain body text, indistinguishable from the paragraph above it. `className="problem"`
 * matched no rule anywhere: the 944-line stylesheet it was written for is gone, and the
 * class outlived it.
 *
 * Bordered and tinted rather than filled. This is a sentence to read and act on, not an
 * alarm — and it uses the destructive family at low weight, which is the treatment a
 * field-level error gets too, so a refusal and a validation failure read as the same
 * KIND of thing at two scales.
 */
const PROBLEM =
  "flex flex-col gap-1.5 rounded-lg border border-surface-negative-border bg-surface-negative-subtle px-4 py-3 text-shell-body";

const PROBLEM_HEADING =
  "m-0 text-[length:var(--type-shell-label-size)] font-medium text-destructive";

export function VerificationAction({
  returnTo,
  purpose,
}: {
  readonly returnTo: string;
  readonly purpose?: "payslip";
}) {
  const t = useT();
  return (
    <a
      href={`/auth/login?reauthenticate=true&return_to=${encodeURIComponent(returnTo)}${purpose === "payslip" ? "&purpose=payslip" : ""}`}
    >
      {t("problem.verifyWithMitra")}
    </a>
  );
}

/** The caller observed the admitted display deadline; this is not an API refusal. */
export function VerificationNotice({
  returnTo: suppliedReturnTo,
  purpose,
}: {
  readonly returnTo?: string;
  readonly purpose?: "payslip";
}) {
  const t = useT();
  const returnTo = suppliedReturnTo ?? `${location.pathname}${location.search}${location.hash}`;
  return (
    <div role="alert" className={PROBLEM}>
      <h2 className={PROBLEM_HEADING}>{t("problem.verificationRequired")}</h2>
      <p className="m-0">{t("problem.verificationHelp")}</p>
      <p>
        <VerificationAction returnTo={returnTo} purpose={purpose} />
      </p>
    </div>
  );
}

export function ProblemNotice({
  failure,
  whileDoing,
  conflictRecovery,
  signInAt,
  returnTo: suppliedReturnTo,
}: ProblemNoticeProps) {
  const t = useT();
  const problem = problemOf(failure);

  // Not an `ApiError` at all: a bug in this bundle, an offline browser, a parse
  // failure. Saying so is more useful than dressing it as a server refusal, because
  // the two send somebody to completely different places.
  if (!problem) {
    return (
      <div role="alert" className={PROBLEM}>
        <h2 className={PROBLEM_HEADING}>{whileDoing ?? t("problem.heading", { title: "" })}</h2>
        <p className="m-0">{t("problem.unreached")}</p>
      </div>
    );
  }

  const known = messageFor(problem.type);
  const verification = problem.code === "insufficient_user_authentication";
  const returnTo = suppliedReturnTo ?? `${location.pathname}${location.search}${location.hash}`;

  return (
    <div role="alert" className={PROBLEM}>
      {/* THE HEADING IS IN THE READER'S LANGUAGE WHEREVER THIS BUNDLE HAS ONE, and it used
          to be riksu's English `title` unconditionally — `problem.heading` is `"{$title}"`
          in BOTH locales, a bare passthrough. So a Turkish reader met a box opening with
          the English words "Not found" above a Turkish sentence, which is the first line
          the product owner read and most of what made it feel foreign.

          THE CLASS MESSAGE IS PROMOTED RATHER THAN A NEW STRING BEING INVENTED, and that
          is the whole shape of this: `problem.notFound` already says the same thing
          `title` says, in the reader's own language, and it was being printed directly
          underneath. A translated heading over that line would have been the SAME sentence
          twice, six pixels apart at two sizes — the defect `trailOf` stops the breadcrumb
          at the parent to avoid. So the sentence moves UP and the body line goes.

          riksu's `title` survives as the fallback for a class this bundle has no message
          for, which is the same rule the detail below keeps: riksu's own words appear
          exactly where nothing here can say it, and nowhere else. */}
      <h2 className={PROBLEM_HEADING}>
        {t("problem.heading", {
          title:
            (problem.code === "conflict" ? conflictRecovery : undefined) ??
            (known ? t(known) : (problem.title ?? whileDoing ?? "")),
        })}
      </h2>

      {/* THE CLASS AND THE SENTENCE, not one or the other.

          This was a ternary: a translated message when the class was known, riksu's own
          words only when it was not. Which reads as careful and threw away the most
          important sentence in the product — because riksu's DOMAIN refusals come back
          as `forbidden` and `conflict`, exactly the classes this has a message for.

          Measured against the running product: refusing to settle a claim answers
          `forbidden` with "this records that the company settled a claim, which is the
          write that says money moved; it needs one of: finance.admin". The reader saw
          "Bunu görme yetkiniz yok." and never learned what would have worked.

          The generic message is the CLASS and stays first, because it is in the
          reader's language. riksu's sentence follows it, marked as English, because it
          is about this instance and nothing in this bundle can translate it. */}
      {/* AND NOT REPEATED HERE. The class sentence is the HEADING now; a copy of it in the
          body was the duplicate this change exists to remove. What follows is riksu's own
          words about THIS instance, which is the half no generic message can carry. */}
      {problem.detail ? (
        <>
          {/* Says, in the reader's language, that what follows is not. Better than
              silently showing English as though it were the interface.

              IT NAMES NEITHER RIKSU NOR RIKSU'S LANGUAGE POLICY, and that is the whole of
              what changed here. It read *"riksu bunu cevap verdiği dilde, İngilizce
              açıkladı"* — riksu in the third person, explaining which language it answers
              in — which the product owner met on `/leave` and read as absurd, correctly: a
              person trying to book leave is not owed an account of the server's language
              choice. The FUNCTION is kept because it is the argued half — a reader who
              cannot read English should be told that is what the next line is, rather than
              meeting it as though it were the interface — and only the voice is gone. */}
          <p className="m-0 text-muted-foreground">{t("problem.untranslated")}</p>
          {/* riksu's own sentence, verbatim: its refusals name what WOULD have worked,
              and re-wording throws away the useful half. */}
          <p className="m-0" lang="en">
            {problem.detail}
          </p>
        </>
      ) : null}

      {/* BACK TO THE WHOLE ADDRESS, QUERY INCLUDED, and this was wrong before a record ever
          rode in one. It sent `location.pathname` alone, so signing in again from a refusal
          landed the reader on the page with whatever the parameter said stripped off —
          already true of anything a screen puts in the query, and now true of the record
          somebody had open when the session expired. `sign-in.tsx` has always sent the
          location rather than the path, and `session.test.ts` pins that a query round-trips
          encoded; this is the one site that had its own copy of the idea and half of it. */}
      {problem.status === 401 && (!verification || !signInAt) ? (
        <p>
          {verification ? (
            <VerificationAction
              returnTo={returnTo}
              purpose={
                problem.acr_values?.split(" ").includes("urn:cogitave:aal2") ? "payslip" : undefined
              }
            />
          ) : (
            <a href={signInAt ?? `/auth/login?return_to=${encodeURIComponent(returnTo)}`}>
              {t("app.signIn")}
            </a>
          )}
        </p>
      ) : null}
    </div>
  );
}
